/**
 * ResultChip — single-source-of-truth chip primitive for the family of
 * "attached structured-data result" affordances surfaced UNDER an agent
 * message bubble.
 *
 * Originally each call-site (``DisplayResultChips`` for SQL/CAD/banking
 * displays, ``TablePreviewChip`` for inline-chart table-degraded
 * payloads) duplicated the same ~90% of Tailwind chrome — bg, border,
 * hover lift, icon-square in ``ando-orange-soft``, dot-separated
 * subtitle, focus-visible ring. Consolidating here means a future visual
 * refresh (radius, hover tone, focus ring offset) is a one-file change
 * and the two surfaces can't drift apart over time. (June 15, 2026 DRY
 * audit.)
 *
 * Variants/options stay minimal on purpose: this primitive renders ONE
 * shape. If a future caller needs a different visual tone (alert / quiet
 * / success), add it via a typed ``tone`` prop rather than forking.
 */

import type { LucideIcon } from "lucide-react";
import type * as React from "react";

import { OpensInPanel, cn } from "@ando/ui";

export interface ResultChipProps {
  /** Lead icon (rendered inside an ``ando-orange-soft`` square). */
  readonly icon: LucideIcon;
  /** Primary label — bold, truncated. */
  readonly label: string;
  /** Optional dot-prefixed subtitle (e.g. row count). */
  readonly subtitle?: string;
  /** Full screen-reader label. Must describe both action + target. */
  readonly ariaLabel: string;
  /** Click handler — typically navigates to the destination tab. */
  readonly onClick: () => void;
  /**
   * Whether to surface the ``OpensInPanel`` arrow. Defaults to ``true``:
   * anything that routes the user to the work area should say so. Set to
   * ``false`` only where the surrounding surface already carries the cue.
   */
  readonly showOpenHint?: boolean;
  /** Optional extra classes on the button (rarely needed). */
  readonly className?: string;
}

const CHIP_CHROME = cn(
  // Layout + sizing — ``min-h-[32px]`` keeps the touch target above the
  // V2 baseline (see `feedback_v1_display_parity_audit_jun14.md`).
  "group inline-flex min-h-[32px] max-w-[280px] items-center gap-1.5 rounded-md border border-hairline bg-white/55 px-2.5 py-1.5",
  "text-[11px] text-ink-2 transition-all",
  "hover:-translate-y-px hover:border-ando-orange/30 hover:bg-white hover:text-ink-1",
  "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ando-orange/45 focus-visible:ring-offset-1",
  "motion-safe:animate-msg-in",
);

export const ResultChip: React.FC<ResultChipProps> = ({
  icon: Icon,
  label,
  subtitle,
  ariaLabel,
  onClick,
  showOpenHint = true,
  className,
}) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      className={cn(CHIP_CHROME, className)}
    >
      <span
        aria-hidden
        className="flex h-4 w-4 shrink-0 items-center justify-center rounded-sm bg-ando-orange-soft text-ando-orange"
      >
        <Icon size={10} strokeWidth={2} />
      </span>
      <span className="truncate font-medium">{label}</span>
      {subtitle ? <span className="shrink-0 text-ink-3 tabular-nums">· {subtitle}</span> : null}
      {showOpenHint ? <OpensInPanel className="text-ink-3" /> : null}
    </button>
  );
};
ResultChip.displayName = "ResultChip";
